import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GetAdminWhatsAppOutboxAPI } from '../../../../api/AdminAPI';

const DEFAULT_PAGINATION = { total: 0, page: 1, limit: 25, pages: 1 };
const DEFAULT_SUMMARY = {
  pending: 0,
  pending_late: 0,
  processing: 0,
  deferred: 0,
  sent_today: 0,
  failed_today: 0,
  canceled_today: 0,
  expired_today: 0,
  next_scheduled_at: null,
  db_now: null,
};
const DEFAULT_FILTER_OPTIONS = { automation_types: [], message_kinds: [] };
const SEARCH_DEBOUNCE_MS = 400;
const AUTO_REFRESH_MS = 30000;

/**
 * useAdminWhatsAppOutbox
 *
 * Hook da tela "Fila WhatsApp" do Admin Geral: resumo + lista paginada/filtrada de
 * company_whatsapp_outbox (admin/whatsapp/outbox.php). Filtros vivem na URL via
 * useSearchParams, no mesmo padrão de useAdminAudit, para que o link possa ser
 * compartilhado.
 *
 * A fila anda sozinha (despachante de 1 minuto), então a tela se atualiza a cada
 * 30 s — só enquanto a aba está visível, para não bater no servidor à toa.
 *
 * @returns {Object} estado e ações da tela
 */
function useAdminWhatsAppOutbox() {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateParams = useCallback((updates, resetPage = true) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });
      if (resetPage) next.delete('page');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Filtros (na URL) ───────────────────────────────────────────────────────
  const status = searchParams.get('status') || 'pending';
  const company = searchParams.get('company') ?? '';
  const automationType = searchParams.get('automation_type') || '';
  const messageKind = searchParams.get('message_kind') || '';
  const priority = searchParams.get('priority') ?? '';
  const dateFrom = searchParams.get('date_from') || '';
  const dateTo = searchParams.get('date_to') || '';
  const late = searchParams.get('late') === '1';
  const page = parseInt(searchParams.get('page'), 10) || 1;

  // Trocar de status desliga o recorte de atrasadas: com late=1 o servidor força `pending`.
  const setStatus = useCallback((value) => updateParams({ status: value, late: '' }), [updateParams]);
  const setAutomationType = useCallback((value) => updateParams({ automation_type: value }), [updateParams]);
  const setMessageKind = useCallback((value) => updateParams({ message_kind: value }), [updateParams]);
  const setPriority = useCallback((value) => updateParams({ priority: value }), [updateParams]);
  const setDateFrom = useCallback((value) => updateParams({ date_from: value }), [updateParams]);
  const setDateTo = useCallback((value) => updateParams({ date_to: value }), [updateParams]);
  const setPage = useCallback((value) => updateParams({ page: value }, false), [updateParams]);

  // "Atrasadas" é uma visão de pendentes: liga o recorte e alinha o status.
  const toggleLate = useCallback(() => {
    updateParams(late ? { late: '' } : { late: '1', status: 'pending' });
  }, [late, updateParams]);

  // ─── Empresa (draft local + debounce -> URL) ───────────────────────────────
  const [companyInput, setCompanyInputState] = useState(company);
  const companyTimer = useRef(null);

  useEffect(() => { setCompanyInputState(company); }, [company]);

  const setCompanyInput = useCallback((value) => {
    setCompanyInputState(value);
    if (companyTimer.current) clearTimeout(companyTimer.current);
    companyTimer.current = setTimeout(() => updateParams({ company: value }), SEARCH_DEBOUNCE_MS);
  }, [updateParams]);

  useEffect(() => () => {
    if (companyTimer.current) clearTimeout(companyTimer.current);
  }, []);

  // ─── Dados (outbox.php) ─────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [filterOptions, setFilterOptions] = useState(DEFAULT_FILTER_OPTIONS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRows = useCallback((options = {}) => {
    const silent = options.silent === true;
    if (!silent) setIsLoading(true);
    setError(null);
    GetAdminWhatsAppOutboxAPI(
      {
        status,
        company,
        automation_type: automationType,
        message_kind: messageKind,
        priority,
        date_from: dateFrom,
        date_to: dateTo,
        late: late ? 1 : '',
        page,
        limit: DEFAULT_PAGINATION.limit,
      },
      (response) => {
        if (!response || response.success === false) {
          setError(response?.error || 'Erro ao carregar a fila de WhatsApp. Tente novamente.');
          setIsLoading(false);
          return;
        }
        setRows(response.rows || []);
        setPagination(response.pagination || { ...DEFAULT_PAGINATION, page });
        setSummary(response.summary || DEFAULT_SUMMARY);
        setFilterOptions(response.filters || DEFAULT_FILTER_OPTIONS);
        setIsLoading(false);
      }
    );
  }, [status, company, automationType, messageKind, priority, dateFrom, dateTo, late, page]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // ─── Atualização automática (30 s, só com a aba visível) ────────────────────
  const fetchRowsRef = useRef(fetchRows);
  useEffect(() => { fetchRowsRef.current = fetchRows; }, [fetchRows]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      fetchRowsRef.current({ silent: true });
    }, AUTO_REFRESH_MS);

    return () => clearInterval(interval);
  }, []);

  return {
    rows,
    pagination,
    summary,
    filterOptions,
    isLoading,
    error,

    status,
    setStatus,
    companyInput,
    setCompanyInput,
    automationType,
    setAutomationType,
    messageKind,
    setMessageKind,
    priority,
    setPriority,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    late,
    toggleLate,
    setPage,

    refresh: fetchRows,
  };
}

export default useAdminWhatsAppOutbox;
